import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { formatCurrency, formatDate, formatDateTime } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Building2, CreditCard, Banknote, Wallet, AlertTriangle, CheckCircle2, XCircle, Loader2, Copy, Phone, Mail } from "lucide-react";
import { toast } from "sonner";
import PoweredByStrip from "@/components/PoweredByStrip";

const API = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "") + "/api";

export default function OfferPublicView() {
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const cancelled = searchParams.get("cancelled");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [payingStripe, setPayingStripe] = useState(false);
  // Stato firma OTP per accettazione (solo metodi non-online)
  const [otpFlow, setOtpFlow] = useState(null); // { step:"requesting"|"input", method, emailHint }
  const [otpCode, setOtpCode] = useState("");
  const [otpBusy, setOtpBusy] = useState(false);

  const requestAcceptOtp = async (method) => {
    setOtpBusy(true);
    try {
      const r = await axios.post(`${API}/public/offer/${token}/accept/request-otp`, { payment_method: method });
      setOtpFlow({ step: "input", method, emailHint: r.data.email_hint });
      setOtpCode("");
      toast.success(`Codice inviato a ${r.data.email_hint}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore invio OTP");
    } finally { setOtpBusy(false); }
  };

  const confirmAcceptOtp = async () => {
    if (!otpFlow || !otpCode || otpCode.length < 4) return;
    setOtpBusy(true);
    try {
      await axios.post(`${API}/public/offer/${token}/accept/confirm`, { otp: otpCode, payment_method: otpFlow.method });
      toast.success("Offerta accettata e firmata ✅");
      setOtpFlow(null);
      setOtpCode("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Codice OTP errato o scaduto");
    } finally { setOtpBusy(false); }
  };

  const load = async () => {
    setLoading(true);
    try {
      // 🆕 Cache-buster: l'iframe in agente passa ?_t=N quando preme "Ricarica",
      // così la GET API non viene servita dalla cache del browser/proxy.
      const ts = new URLSearchParams(window.location.search).get("_t") || Date.now();
      const r = await axios.get(`${API}/public/offer/${token}?_=${ts}`);
      setData(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Offerta non disponibile");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [token]);

  const payWithStripe = async () => {
    setPayingStripe(true);
    try {
      const r = await axios.post(`${API}/public/offer/${token}/stripe-checkout`, { origin_url: window.location.origin });
      window.location.href = r.data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore pagamento");
      setPayingStripe(false);
    }
  };

  const copyIban = (iban) => {
    navigator.clipboard?.writeText(iban).then(() => toast.success("IBAN copiato"));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
      </div>
    );
  }
  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="bg-white border border-rose-200 rounded-lg p-8 max-w-md text-center">
          <XCircle className="h-12 w-12 mx-auto text-rose-500" />
          <h1 className="text-xl font-semibold mt-3">Offerta non disponibile</h1>
          <p className="text-sm text-slate-600 mt-2">{error}</p>
        </div>
      </div>
    );
  }
  if (!data) return null;

  const { offer, client, agency, payment_methods, payment_labels } = data;
  const labels = payment_labels || {};
  const isPaid = offer.status === "pagata";
  const isAccepted = offer.status === "accettata";
  const isExpired = offer.days_left < 0 || offer.status === "annullata";

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50/30 py-6 px-4 sm:py-12" data-testid="offer-public-view">
      <div className="max-w-2xl mx-auto">
        <PoweredByStrip testId="opv-powered-by" />
        {/* Header agenzia */}
        <div className="bg-[#0A192F] text-white px-5 sm:px-8 py-5 flex items-center gap-3">
          <div className="h-12 w-12 shrink-0 rounded-md bg-blue-600 flex items-center justify-center overflow-hidden">
            {agency.logo_url ? <img src={agency.logo_url} alt="logo" className="h-full w-full object-cover" /> : <Building2 className="h-6 w-6" />}
          </div>
          <div className="min-w-0">
            <div className="font-display font-semibold tracking-tight truncate">{agency.legal_name || agency.name}</div>
            <div className="text-xs text-slate-300">Offerta finale · {offer.offer_number}</div>
          </div>
        </div>

        <div className="bg-white border border-slate-200 border-t-0 rounded-b-xl px-5 sm:px-8 py-6 space-y-5">
          {/* Stato pagamento */}
          {sessionId && (
            <div data-testid="payment-success-banner" className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-start gap-2 text-sm text-green-900">
              <CheckCircle2 className="h-5 w-5 shrink-0 mt-0.5" />
              <div>
                <strong>Pagamento ricevuto.</strong> L&apos;agenzia procederà a breve con l&apos;emissione della tua polizza.
              </div>
            </div>
          )}
          {cancelled && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-2 text-sm text-amber-900">
              <AlertTriangle className="h-5 w-5 shrink-0 mt-0.5" />
              <div><strong>Pagamento annullato.</strong> Puoi riprovare quando vuoi prima della scadenza.</div>
            </div>
          )}

          {/* Saluto */}
          <div>
            <p className="text-xs uppercase tracking-[0.15em] text-blue-700 font-semibold">Gentile</p>
            <h1 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight mt-1 break-words">
              {client.surname} {client.name}
            </h1>
          </div>

          {/* Importo da pagare — 🆕 mostra piano rateale personalizzato se rate diverse, altrimenti premio per rata */}
          <div className="bg-slate-50 border-l-4 border-slate-900 rounded-r-lg p-5" data-testid="offer-amount-block">
            {(() => {
              const raten = parseInt(offer.installments_count, 10);
              const total = parseFloat(offer.amount);
              const amts = Array.isArray(offer.installments_amounts)
                ? offer.installments_amounts.map((x) => Number(x) || 0)
                : [];
              // 🆕 Piano rateale personalizzato valido: N rate, tutte > 0, non uniformi
              const hasCustomPlan =
                amts.length >= 2 &&
                Number.isFinite(raten) &&
                amts.length === raten &&
                amts.every((a) => a > 0) &&
                !amts.every((a) => Math.abs(a - amts[0]) < 0.005);
              if (hasCustomPlan) {
                const sumPlan = amts.reduce((s, a) => s + a, 0);
                const _frazLbl = ({
                  annuale: "ANNUALE",
                  semestrale: "SEMESTRALE",
                  quadrimestrale: "QUADRIMESTRALE",
                  trimestrale: "TRIMESTRALE",
                  mensile: "MENSILE",
                })[String(offer.frazionamento || "").toLowerCase()];
                return (
                  <>
                    <div className="text-xs uppercase tracking-wide text-slate-500">
                      Importo totale · Piano rateale personalizzato ({raten} rate)
                      {_frazLbl && <span className="ml-2 text-emerald-700 font-semibold">· {_frazLbl}</span>}
                    </div>
                    <div className="text-4xl sm:text-5xl font-display font-bold text-slate-900 tabular-nums mt-1" data-testid="offer-amount-display">
                      {formatCurrency(sumPlan)}
                    </div>
                    <div className="text-sm text-slate-700 mt-2 tabular-nums flex flex-wrap gap-x-3 gap-y-1" data-testid="offer-plan-breakdown">
                      {amts.map((a, i) => (
                        <span key={i}>
                          <strong>{i + 1}°</strong> {formatCurrency(a)}
                          {i < amts.length - 1 ? " ·" : ""}
                        </span>
                      ))}
                    </div>
                  </>
                );
              }
              const hasInst = Number.isFinite(raten) && raten > 1 && Number.isFinite(total) && total > 0;
              if (hasInst) {
                const perRate = Math.round((total / raten) * 100) / 100;
                const _frazLbl2 = ({
                  annuale: "ANNUALE",
                  semestrale: "SEMESTRALE",
                  quadrimestrale: "QUADRIMESTRALE",
                  trimestrale: "TRIMESTRALE",
                  mensile: "MENSILE",
                })[String(offer.frazionamento || "").toLowerCase()];
                return (
                  <>
                    <div className="text-xs uppercase tracking-wide text-slate-500">
                      Premio per rata · {raten} rate
                      {_frazLbl2 && <span className="ml-2 text-emerald-700 font-semibold">· {_frazLbl2}</span>}
                    </div>
                    <div className="text-4xl sm:text-5xl font-display font-bold text-slate-900 tabular-nums mt-1" data-testid="offer-amount-display">
                      {formatCurrency(perRate)}
                      <span className="text-lg font-medium text-slate-500 ml-2">/rata</span>
                    </div>
                    <div className="text-sm text-slate-700 mt-2 tabular-nums">
                      × <strong>{raten} rate</strong> = <strong>{formatCurrency(total)}</strong> totale
                    </div>
                  </>
                );
              }
              return (
                <>
                  <div className="text-xs uppercase tracking-wide text-slate-500">
                    Importo da pagare
                    {/* 🆕 10/07/2026 — Indicazione frazionamento accanto al titolo (aiuta a capire se annuale/semestrale/…) */}
                    {offer.frazionamento && (
                      <span className="ml-2 text-emerald-700 font-semibold" data-testid="offer-frazionamento-inline">
                        · {{
                          annuale: "ANNUALE",
                          semestrale: "SEMESTRALE",
                          quadrimestrale: "QUADRIMESTRALE",
                          trimestrale: "TRIMESTRALE",
                          mensile: "MENSILE",
                        }[String(offer.frazionamento).toLowerCase()] || String(offer.frazionamento).toUpperCase()}
                      </span>
                    )}
                  </div>
                  <div className="text-4xl sm:text-5xl font-display font-bold text-slate-900 tabular-nums mt-1" data-testid="offer-amount-display">
                    {formatCurrency(offer.amount)}
                  </div>
                </>
              );
            })()}
            <div className="text-sm text-slate-600 mt-2">
              Scadenza: <strong data-testid="offer-deadline">{formatDate(offer.valid_until)}</strong>
              {offer.days_left >= 0 && !isPaid && (
                <span className="ml-2 text-amber-700">· {offer.days_left} giorn{offer.days_left === 1 ? "o" : "i"} rimanent{offer.days_left === 1 ? "e" : "i"}</span>
              )}
            </div>
          </div>

          {/* 🚗 Oggetto dell'offerta (veicolo + targa, sempre visibile se disponibile) */}
          {(offer.vehicle_plate || offer.vehicle_brand || offer.vehicle_model) && (
            <div data-testid="offer-vehicle-box" className="bg-blue-50 border-l-4 border-blue-600 rounded-r-lg p-4">
              <div className="text-xs uppercase tracking-wide font-semibold text-blue-700 mb-1">🚗 Oggetto dell&apos;offerta</div>
              <div className="text-base sm:text-lg font-medium text-blue-900 flex flex-wrap items-center gap-x-3 gap-y-1">
                {(offer.vehicle_brand || offer.vehicle_model) && (
                  <span>{(offer.vehicle_brand || "").trim()} {(offer.vehicle_model || "").trim()}</span>
                )}
                {offer.vehicle_plate && (
                  <span>
                    Targa <strong className="font-mono tracking-wider bg-white px-2 py-0.5 rounded border border-blue-200">{offer.vehicle_plate}</strong>
                  </span>
                )}
              </div>
            </div>
          )}

          {/* 🆕 Frazionamento (se presente sull'offerta) */}
          {offer.frazionamento && (
            <div data-testid="offer-frazionamento-box" className="bg-emerald-50 border-l-4 border-emerald-600 rounded-r-lg p-4">
              <div className="text-xs uppercase tracking-wide font-semibold text-emerald-700 mb-1">📅 Frazionamento pagamento</div>
              <div className="text-base sm:text-lg font-medium text-emerald-900">
                {{
                  annuale: "Annuale (1 rata/anno)",
                  semestrale: "Semestrale (2 rate/anno)",
                  quadrimestrale: "Quadrimestrale (3 rate/anno)",
                  trimestrale: "Trimestrale (4 rate/anno)",
                  mensile: "Mensile (12 rate/anno)",
                }[String(offer.frazionamento).toLowerCase()] || offer.frazionamento}
              </div>
              {/* 🆕 Prossime scadenze — calcolo indicativo delle date di ciascuna rata */}
              {(() => {
                const raten = parseInt(offer.installments_count, 10);
                const fraz = String(offer.frazionamento || "").toLowerCase();
                if (!Number.isFinite(raten) || raten < 2 || fraz === "annuale") return null;
                const monthsMap = { semestrale: 6, quadrimestrale: 4, trimestrale: 3, mensile: 1 };
                const step = monthsMap[fraz];
                if (!step) return null;
                const startStr = offer.effective_date || null;
                let start = startStr ? new Date(startStr) : new Date();
                if (Number.isNaN(start.getTime())) start = new Date();
                const amts = Array.isArray(offer.installments_amounts) ? offer.installments_amounts.map((x) => Number(x) || 0) : [];
                const total = parseFloat(offer.amount) || 0;
                const uniform = amts.length !== raten ? Array(raten).fill(total / raten) : amts;
                const dates = [];
                for (let i = 0; i < raten; i++) {
                  const d = new Date(start);
                  d.setMonth(d.getMonth() + step * i);
                  dates.push(d);
                }
                return (
                  <div className="mt-3 border-t border-emerald-200 pt-2" data-testid="offer-payment-schedule">
                    <div className="text-[11px] uppercase tracking-wider font-semibold text-emerald-800 mb-1.5">Prossime scadenze indicative</div>
                    <div className="grid gap-1">
                      {dates.map((d, i) => (
                        <div key={i} className="flex items-center justify-between text-sm text-emerald-900 tabular-nums">
                          <span><strong className="mr-1">{i + 1}° rata</strong> · {d.toLocaleDateString("it-IT")}</span>
                          <span className="font-semibold">{formatCurrency(uniform[i])}</span>
                        </div>
                      ))}
                    </div>
                    <p className="text-[10px] italic text-emerald-700 mt-1.5">Le date esatte saranno indicate in polizza.</p>
                  </div>
                );
              })()}
            </div>
          )}

          {/* 🆕 Tipo di guida (se presente sull'offerta) */}
          {offer.driving_type && (
            <div data-testid="offer-driving-type-box" className="bg-blue-50 border-l-4 border-blue-600 rounded-r-lg p-4">
              <div className="text-xs uppercase tracking-wide font-semibold text-blue-700 mb-1">🪪 Tipo di guida</div>
              <div className="text-base sm:text-lg font-medium text-blue-900">
                {{ esclusiva: "Esclusiva", esperta: "Esperta", libera: "Libera" }[String(offer.driving_type).toLowerCase()] || offer.driving_type}
              </div>
              <div className="mt-2 text-[12px] text-blue-800/90 leading-relaxed">
                {{
                  esclusiva: "Può guidare solo l'intestatario del contratto. È la formula più economica ma con vincoli rigidi: in caso di sinistro alla guida di un altro conducente, può essere applicata una penale (rivalsa).",
                  esperta: "Possono guidare solo conducenti con almeno 26 anni di età (o secondo i termini di compagnia). Premio intermedio, copre familiari conviventi adulti.",
                  libera: "Chiunque sia in possesso di regolare patente può guidare il veicolo, senza limiti di età. È la formula più flessibile, con premio leggermente più alto.",
                }[String(offer.driving_type).toLowerCase()] || ""}
              </div>
            </div>
          )}

          {/* Avviso 5 giorni */}
          {!isPaid && !isExpired && (
            <div className="bg-amber-50 border border-amber-300 rounded-lg p-4 flex items-start gap-3 text-sm text-amber-900" data-testid="offer-warning">
              <AlertTriangle className="h-5 w-5 shrink-0 mt-0.5" />
              <div>
                <strong>Attenzione:</strong> per procedere all&apos;emissione della polizza è necessario pagare entro la data di scadenza (massimo 5 giorni). Se l&apos;offerta non viene pagata in tempo, potrà essere annullata e l&apos;importo potrebbe variare successivamente.
              </div>
            </div>
          )}

          {/* Coperture (solo kind=coverage) e Voci accessorie (kind=line_item) — separate visivamente */}
          {(() => {
            const covs = (offer.coverages || []).filter((c) => (c.kind || "coverage") !== "line_item");
            const lineItems = (offer.coverages || []).filter((c) => (c.kind || "coverage") === "line_item");
            return (
              <>
                {covs.length > 0 && (
                  <div>
                    <h2 className="font-display text-lg font-semibold mb-2">Coperture incluse</h2>
                    <div className="space-y-2">
                      {covs.map((c, i) => (
                        <div key={`cov-${i}`} className="bg-white border border-slate-200 rounded-lg p-3 flex items-start gap-3" data-testid={`offer-cov-${i}`}>
                          <CheckCircle2 className="h-5 w-5 text-emerald-600 shrink-0 mt-0.5" />
                          <div className="flex-1 min-w-0">
                            <div className="font-semibold">{c.name}</div>
                            <div className="text-xs text-slate-600 mt-0.5 flex flex-wrap gap-x-3 gap-y-1">
                              {c.sum_insured ? <span>Massimale: <strong>{formatCurrency(c.sum_insured)}</strong></span> : null}
                              {c.premium ? <span>Premio: <strong>{formatCurrency(c.premium)}</strong></span> : null}
                              {c.deductible ? <span>Franchigia: <strong>{c.deductible}</strong></span> : null}
                            </div>
                            {c.description && <div className="text-xs text-slate-500 mt-1">{c.description}</div>}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {lineItems.length > 0 && (
                  <div>
                    <h2 className="font-display text-lg font-semibold mb-2">Voci accessorie</h2>
                    <div className="space-y-2">
                      {lineItems.map((c, i) => (
                        <div key={`li-${i}`} className="bg-slate-50 border border-slate-200 rounded-lg p-3 flex items-start gap-3" data-testid={`offer-line-item-${i}`}>
                          <CheckCircle2 className="h-5 w-5 text-indigo-500 shrink-0 mt-0.5" />
                          <div className="flex-1 min-w-0">
                            <div className="font-semibold">{c.name}</div>
                            <div className="text-xs text-slate-600 mt-0.5 flex flex-wrap gap-x-3 gap-y-1">
                              {c.premium ? <span>Importo: <strong>{formatCurrency(c.premium)}</strong></span> : null}
                            </div>
                            {c.description && <div className="text-xs text-slate-500 mt-1">{c.description}</div>}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            );
          })()}

          {offer.notes && (
            <div className="bg-slate-50 border border-slate-200 rounded p-3 text-sm">
              <div className="text-xs uppercase tracking-wide text-slate-500 font-semibold mb-1">Note</div>
              {offer.notes}
            </div>
          )}

          {/* Metodi pagamento */}
          {!isPaid && !isExpired && (
            <div>
              <h2 className="font-display text-lg font-semibold mb-3">Forme di pagamento disponibili</h2>
              {isAccepted && (
                <div data-testid="offer-accepted-banner" className="mb-3 rounded-lg border border-emerald-200 bg-emerald-50 p-4 flex items-start gap-2 text-sm text-emerald-900">
                  <CheckCircle2 className="h-5 w-5 shrink-0 mt-0.5" />
                  <div>
                    <strong>Offerta accettata e firmata</strong> il {formatDateTime(offer.accepted_at)}.<br />
                    {offer.accepted_payment_method === "bank_transfer" && "Procedi ora con il bonifico secondo le istruzioni qui sotto."}
                    {offer.accepted_payment_method === "cash" && "Vieni in agenzia con il numero offerta per saldare."}
                  </div>
                </div>
              )}
              <div className="space-y-3">
                {payment_methods.stripe && (
                  <div>
                    <Button
                      data-testid="pay-stripe-btn"
                      onClick={payWithStripe}
                      disabled={payingStripe}
                      className="w-full bg-blue-600 hover:bg-blue-700 text-white py-6 text-base justify-start"
                    >
                      <CreditCard className="h-5 w-5 mr-3" />
                      <div className="text-left flex-1">
                        <div className="font-semibold">{payingStripe ? "Reindirizzamento…" : (labels.stripe_label || "Paga ora con carta")}</div>
                        <div className="text-xs opacity-90">{labels.stripe_description || "Pagamento sicuro Stripe"}</div>
                      </div>
                    </Button>
                    {/* 🆕 22/07/2026 — Breakdown trasparente commissione Stripe */}
                    {offer.stripe_breakdown && (
                      <div
                        className="mt-2 bg-blue-50/60 border border-blue-200 rounded-md p-2.5 text-[11px] tabular-nums"
                        data-testid="offer-stripe-fee-breakdown"
                      >
                        <div className="flex justify-between text-slate-600">
                          <span>Polizza</span>
                          <span>{formatCurrency(offer.stripe_breakdown.net_amount)}</span>
                        </div>
                        <div className="flex justify-between text-slate-600">
                          <span>Commissione servizio online</span>
                          <span>+ {formatCurrency(offer.stripe_breakdown.fee_amount)}</span>
                        </div>
                        <div className="flex justify-between font-bold text-slate-900 pt-1 mt-1 border-t border-blue-200">
                          <span>Totale addebitato</span>
                          <span className="text-blue-700">{formatCurrency(offer.stripe_breakdown.gross_amount)}</span>
                        </div>
                      </div>
                    )}
                  </div>
                )}
                {payment_methods.bank_transfer && agency.iban && (
                  <div data-testid="pay-bank-block" className="bg-white border border-slate-200 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <Banknote className="h-5 w-5 text-emerald-600 mt-0.5" />
                      <div className="flex-1">
                        <div className="font-semibold">{labels.bank_transfer_label || "Bonifico bancario"}</div>
                        <div className="text-xs text-slate-600 mt-1">{labels.bank_transfer_description || "Effettua un bonifico verso questo IBAN. Una volta ricevuto, l'agenzia procederà all'emissione."}</div>
                        <div className="mt-2 bg-slate-50 rounded p-2 text-sm font-mono break-all flex items-center justify-between gap-2">
                          <span data-testid="iban-display">{agency.iban}</span>
                          <button data-testid="copy-iban-btn" onClick={() => copyIban(agency.iban)} className="p-1 hover:bg-slate-200 rounded shrink-0" title="Copia IBAN">
                            <Copy className="h-3.5 w-3.5" />
                          </button>
                        </div>
                        {agency.bank_beneficiary && <div className="text-xs text-slate-500 mt-1">Beneficiario: <strong>{agency.bank_beneficiary}</strong></div>}
                        {agency.bank_name && <div className="text-xs text-slate-500 mt-1">Banca: {agency.bank_name}</div>}
                        {agency.bic_swift && <div className="text-xs text-slate-500 mt-1">BIC/SWIFT: <span className="font-mono">{agency.bic_swift}</span></div>}
                        <div className="text-xs text-slate-500 mt-1">Causale: <strong>{offer.offer_number} - {client.surname}</strong></div>
                        {!isAccepted && (
                          <Button
                            data-testid="accept-bank-btn"
                            onClick={() => requestAcceptOtp("bank_transfer")}
                            disabled={otpBusy}
                            className="mt-3 w-full bg-emerald-600 hover:bg-emerald-700 text-white"
                          >
                            ✍️ Accetta e firma con OTP
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                )}
                {payment_methods.cash && (
                  <div data-testid="pay-cash-block" className="bg-white border border-slate-200 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <Wallet className="h-5 w-5 text-amber-600 mt-0.5" />
                      <div className="flex-1">
                        <div className="font-semibold">{labels.cash_label || "Contanti in agenzia"}</div>
                        <div className="text-xs text-slate-600 mt-1">{labels.cash_description || `Vieni a pagare in contanti presso la nostra sede.`} Porta con te il numero offerta <strong className="font-mono">{offer.offer_number}</strong>.</div>
                        {(agency.address || agency.city) && (
                          <div className="text-xs text-slate-500 mt-2">📍 {agency.address} {agency.cap} {agency.city}</div>
                        )}
                        {agency.phone && <div className="text-xs text-slate-500 mt-1 inline-flex items-center gap-1"><Phone className="h-3 w-3" /> {agency.phone}</div>}
                        {!isAccepted && (
                          <Button
                            data-testid="accept-cash-btn"
                            onClick={() => requestAcceptOtp("cash")}
                            disabled={otpBusy}
                            className="mt-3 w-full bg-emerald-600 hover:bg-emerald-700 text-white"
                          >
                            ✍️ Accetta e firma con OTP
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Dialog inserimento OTP per firma accettazione */}
          {otpFlow && otpFlow.step === "input" && (
            <div className="fixed inset-0 z-50 bg-slate-900/60 flex items-center justify-center p-4" data-testid="accept-otp-dialog">
              <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6 space-y-4">
                <div>
                  <h3 className="font-display text-xl font-semibold flex items-center gap-2">
                    <CheckCircle2 className="h-6 w-6 text-emerald-600" /> Firma con codice OTP
                  </h3>
                  <p className="text-sm text-slate-600 mt-2">
                    Abbiamo inviato un codice a 6 cifre a <strong>{otpFlow.emailHint}</strong>.
                    Inseriscilo qui sotto per <strong>firmare l&apos;accettazione</strong> dell&apos;offerta {offer.offer_number}
                    {" "}({otpFlow.method === "bank_transfer" ? "Bonifico" : "Contanti in agenzia"}).
                  </p>
                </div>
                <div>
                  <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Codice OTP</label>
                  <input
                    data-testid="accept-otp-input"
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={6}
                    value={otpCode}
                    onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                    placeholder="6 cifre"
                    className="w-full text-center text-3xl font-mono tracking-[0.5em] py-3 border border-slate-300 rounded-lg focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 outline-none mt-1"
                    autoFocus
                  />
                  <button
                    type="button"
                    onClick={() => requestAcceptOtp(otpFlow.method)}
                    disabled={otpBusy}
                    className="text-xs text-blue-600 hover:underline mt-2"
                    data-testid="accept-otp-resend"
                  >
                    Non l&apos;ho ricevuto · Reinvia
                  </button>
                </div>
                <div className="flex gap-2 justify-end pt-2 border-t border-slate-100">
                  <Button variant="outline" onClick={() => { setOtpFlow(null); setOtpCode(""); }} disabled={otpBusy}>Annulla</Button>
                  <Button
                    data-testid="accept-otp-confirm"
                    onClick={confirmAcceptOtp}
                    disabled={otpBusy || otpCode.length < 4}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white"
                  >
                    {otpBusy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                    Firma e accetta
                  </Button>
                </div>
              </div>
            </div>
          )}

          {isPaid && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-5 text-center" data-testid="offer-paid-banner">
              <CheckCircle2 className="h-10 w-10 text-green-600 mx-auto" />
              <div className="font-semibold mt-2">Offerta pagata</div>
              <div className="text-sm text-slate-600 mt-1">
                Pagamento confermato il {formatDateTime(offer.paid_at)}.<br />
                L&apos;agenzia ti contatterà per l&apos;emissione della polizza.
              </div>
            </div>
          )}

          {isExpired && !isPaid && (
            <div className="bg-rose-50 border border-rose-200 rounded-lg p-5 text-center">
              <XCircle className="h-10 w-10 text-rose-600 mx-auto" />
              <div className="font-semibold mt-2">Offerta scaduta o annullata</div>
              <div className="text-sm text-slate-600 mt-1">Contatta l&apos;agenzia per ricevere una nuova offerta aggiornata.</div>
            </div>
          )}

          {/* Footer agenzia */}
          <div className="border-t border-slate-200 pt-4 text-xs text-slate-500 space-y-1">
            <div className="font-semibold text-slate-700">{agency.legal_name || agency.name}</div>
            {agency.address && <div>{agency.address} · {agency.cap} {agency.city}</div>}
            <div className="flex flex-wrap gap-3">
              {agency.phone && <span className="inline-flex items-center gap-1"><Phone className="h-3 w-3" /> {agency.phone}</span>}
              {agency.email && <span className="inline-flex items-center gap-1 break-all"><Mail className="h-3 w-3" /> {agency.email}</span>}
            </div>
            {agency.vat_number && <div>P.IVA {agency.vat_number}</div>}
          </div>
        </div>
      </div>
    </div>
  );
}
